import { Icons } from '@/libs/components/src/lib/icons';
import { INVOICE_REV_CURRENT } from '@/libs/core/src/lib/constants/globals';
import { Button } from '@chiroup/components/Button';
import { Checkbox } from '@chiroup/components/form/Checkbox';
import { Input } from '@chiroup/components/form/Input';
import { Select } from '@chiroup/components/form/Select';
import { Textarea } from '@chiroup/components/form/Textarea';
import { BillingKeyPanelParams, InvoicePanelParams } from '@chiroup/core/types/Devtools.type';
import { FormError } from '@chiroup/core/types/ErrorResponse.type';
import { AllInvoiceStatusOptions } from '@chiroup/core/types/Invoice.type';
import React, { useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import useDevtools from '../../../hooks/useDevtools';

const defaultValues: InvoicePanelParams = {
  options: { convertIssuesToNotes: true },
};

const billingKeyDefaultValues: BillingKeyPanelParams = {
  clinicId: undefined,
  limit: 100,
  batchSize: 5,
};

const backupTableDefaultValues = {
  tableName: '',
  limit: undefined as number | undefined,
};

type ProcessingResult = {
  input: string;
  billingKey: string;
  success: boolean;
  error?: string;
  durationMs?: number;
  magicResult?: any;
  actions?: any[];
  touched?: boolean;
  warnings?: any[];
  errors?: any[];
};

const extractBillingKey = (input: string): string => {
  const trimmed = input.trim();

  // If it looks like a URL with /consolidated/transaction/ pattern
  if (trimmed.includes('/consolidated/transaction/')) {
    const match = trimmed.match(/\/consolidated\/transaction\/([^#]+)/);

    if (match) {
      return match[1];
    }
  }

  // Otherwise assume it's already a billing key
  return trimmed;
};

const formatDuration = (ms: number): string => {
  if (ms < 1000) {
    return `${ms}ms`;
  } else if (ms < 60000) {
    return `${(ms / 1000).toFixed(1)}s`;
  } else {
    const minutes = Math.floor(ms / 60000);
    const seconds = ((ms % 60000) / 1000).toFixed(1);

    return `${minutes}m ${seconds}s`;
  }
};

const InvoicePanel: React.FC = () => {
  const { isFetching, move } = useDevtools('invoice-panel');
  const billingKeyDevtools = useDevtools('billing-key-panel');
  const {
    processSingle,
    getOutdatedInvoices,
    getOutdatedInvoicesCount,
    getUnrebuiltFromTable,
    getUnrebuiltCountFromTable,
  } = billingKeyDevtools as any;

  // Invoice panel form
  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<InvoicePanelParams>({ defaultValues });

  // Billing key panel form
  const {
    handleSubmit: handleBillingKeySubmit,
    control: billingKeyControl,
    formState: { errors: billingKeyErrors },
    getValues: getBillingKeyValues,
    setValue: setBillingKeyValue,
    watch,
  } = useForm<BillingKeyPanelParams>({ defaultValues: billingKeyDefaultValues });

  // Backup table form
  const {
    control: backupTableControl,
    formState: { errors: backupTableErrors },
    getValues: getBackupTableValues,
  } = useForm({ defaultValues: backupTableDefaultValues });

  // Billing key panel state
  const [isProcessing, setIsProcessing] = useState(false);
  const [isLoadingOutdated, setIsLoadingOutdated] = useState(false);
  const [isLoadingCount, setIsLoadingCount] = useState(false);
  const [isLoadingFromTable, setIsLoadingFromTable] = useState(false);
  const [isCountingFromTable, setIsCountingFromTable] = useState(false);
  const [tableCountResults, setTableCountResults] = useState<{
    tableName: string;
    count: number;
  } | null>(null);
  const [countResults, setCountResults] = useState<{
    clinicId: number;
    uniqueBillingKeys: number;
    totalOutdatedInvoices: number;
    hasDuplicates: boolean;
    duplicateBillingKeys: number;
    hasMixedRevisions: boolean;
    mixedRevisionBillingKeys: number;
  } | null>(null);
  const [results, setResults] = useState<ProcessingResult[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [shouldStop, setShouldStop] = useState(false);
  const [batchStartTime, setBatchStartTime] = useState<number | null>(null);
  const [batchDuration, setBatchDuration] = useState<number | null>(null);
  const [duplicateWarnings, setDuplicateWarnings] = useState<
    { billingKey: string; totalInvoices: number; outdatedCount: number; currentCount: number }[]
  >([]);
  const [mixedRevisionWarnings, setMixedRevisionWarnings] = useState<
    {
      billingKey: string;
      totalInvoices: number;
      outdatedCount: number;
      currentCount: number;
      minRev: number;
      maxRev: number;
      allRevs: string;
    }[]
  >([]);
  const cancelRef = useRef(false);

  const batchSize = watch('batchSize');

  const onSubmit = (data: InvoicePanelParams) => {
    if (move) {
      move(data)
        .then(res => res)
        .catch(e => console.error(e));
    } else {
      console.error('move is not defined');
    }
  };

  const commentOutProcessedLine = (originalInput: string, targetLine: string) => {
    const allLines = originalInput.split('\n');
    const updatedLines = allLines.map(line => {
      const trimmedLine = line.trim();

      if (trimmedLine === targetLine && !line.startsWith('# ')) {
        return `# ${line}`;
      }

      return line;
    });

    return updatedLines.join('\n');
  };

  const onBillingKeySubmit = async (data: BillingKeyPanelParams) => {
    if (!data.sBillingKeys) return;

    const allLines = data.sBillingKeys.split('\n');
    const lines = allLines
      .map(line => line.trim())
      .filter(line => line.length > 0 && !line.startsWith('# '));

    if (lines.length === 0) return;

    const batchSize = Math.max(1, Math.min(10, data.batchSize || 5));

    setIsProcessing(true);
    setShouldStop(false);
    cancelRef.current = false;
    setResults([]);
    setCurrentIndex(0);
    setBatchDuration(null);
    setDuplicateWarnings([]);
    setMixedRevisionWarnings([]);
    setCountResults(null);

    const batchStart = Date.now();
    setBatchStartTime(batchStart);

    const processResults: ProcessingResult[] = [];
    let currentTextareaContent = data.sBillingKeys;

    for (let i = 0; i < lines.length; i += batchSize) {
      if (cancelRef.current) break;

      const batchLines = lines.slice(i, i + batchSize);
      const billingKeys = batchLines.map(line => extractBillingKey(line));

      setCurrentIndex(i + batchLines.length);

      try {
        console.log(
          `Processing batch ${Math.floor(i / batchSize) + 1}: ${billingKeys.length} items (${billingKeys.join(', ')})`
        );

        const backupTableName = getBackupTableValues().tableName;
        const batchResult = await processSingle({
          billingKeys,
          tableName:
            backupTableName && backupTableName.trim() !== '' ? backupTableName.trim() : undefined,
        });

        if (batchResult.success && batchResult.results) {
          for (let j = 0; j < batchResult.results.length; j++) {
            const result = batchResult.results[j];
            const originalInput = batchLines[j];

            const processResult: ProcessingResult = {
              input: originalInput,
              billingKey: result.billingKey,
              success: result.success,
              durationMs: result.durationMs,
              magicResult: result.magicResult,
              actions: result.actions || [],
              touched: result.touched || false,
              warnings: result.warnings || [],
              errors: result.errors || [],
              error: result.error,
            };

            processResults.push(processResult);
            currentTextareaContent = commentOutProcessedLine(currentTextareaContent, originalInput);
          }

          setResults([...processResults]);
          setBillingKeyValue('sBillingKeys', currentTextareaContent);

          console.log(
            `✅ Batch processed: ${batchResult.summary.successful} successful, ${batchResult.summary.failed} failed (${formatDuration(batchResult.batchDuration)})`
          );
        }
      } catch (error: any) {
        console.error(`❌ Failed to process batch:`, error);

        for (const originalInput of batchLines) {
          const processResult: ProcessingResult = {
            input: originalInput,
            billingKey: extractBillingKey(originalInput),
            success: false,
            error: error.message || 'Batch processing failed',
          };

          processResults.push(processResult);
          currentTextareaContent = commentOutProcessedLine(currentTextareaContent, originalInput);
        }

        setResults([...processResults]);
        setBillingKeyValue('sBillingKeys', currentTextareaContent);
      }

      await new Promise(resolve => setTimeout(resolve, 200));
    }

    const batchEnd = Date.now();
    const totalBatchDuration = batchEnd - batchStart;
    setBatchDuration(totalBatchDuration);

    setIsProcessing(false);
    setCurrentIndex(0);

    const successful = processResults.filter(r => r.success).length;
    const withIssues = processResults.filter(
      r => !r.success && r.warnings && r.warnings.length > 0 && (!r.errors || r.errors.length === 0)
    ).length;
    const failed = processResults.filter(
      r =>
        !r.success && (!r.warnings || r.warnings.length === 0 || (r.errors && r.errors.length > 0))
    ).length;

    const totalProcessingTime = processResults.reduce((sum, r) => sum + (r.durationMs || 0), 0);
    const avgDuration = processResults.length > 0 ? totalProcessingTime / processResults.length : 0;

    console.log(
      `🏁 Batch processing complete (${formatDuration(totalBatchDuration)}): ${successful} successful, ${withIssues} with issues, ${failed} failed | Avg: ${formatDuration(avgDuration)} | Batch size: ${batchSize}`
    );
  };

  const handleStop = () => {
    setShouldStop(true);
    cancelRef.current = true;
  };

  const handleCountOutdated = async () => {
    const formData = getBillingKeyValues();

    if (!formData.clinicId) {
      alert('Please enter a Clinic ID');

      return;
    }

    setIsLoadingCount(true);
    setCountResults(null);

    try {
      const response = await getOutdatedInvoicesCount({
        clinicId: formData.clinicId,
      });

      setCountResults(response);

      console.log(`📊 Count results for clinic ${formData.clinicId}:`, response);
    } catch (error: any) {
      console.error('❌ Failed to count outdated invoices:', error);
      alert(`Failed to count outdated invoices: ${error.message}`);
    } finally {
      setIsLoadingCount(false);
    }
  };

  const handleLoadOutdated = async () => {
    const formData = getBillingKeyValues();

    if (!formData.clinicId) {
      alert('Please enter a Clinic ID');

      return;
    }

    setIsLoadingOutdated(true);
    setDuplicateWarnings([]);
    setMixedRevisionWarnings([]);

    try {
      const response = await getOutdatedInvoices({
        clinicId: formData.clinicId,
        limit: formData.limit || undefined,
      });

      if (response.billingKeys && response.billingKeys.length > 0) {
        const billingKeysText = response.billingKeys.join('\n');
        setBillingKeyValue('sBillingKeys', billingKeysText);

        if (response.hasDuplicates && response.duplicates?.length > 0) {
          setDuplicateWarnings(response.duplicates);
        }

        if (response.hasMixedRevisions && response.mixedRevisions?.length > 0) {
          setMixedRevisionWarnings(response.mixedRevisions);
        }

        console.log(
          `✅ Loaded ${response.count} unique billing keys (${response.totalOutdatedInvoices} outdated invoices) for clinic ${formData.clinicId}`
        );

        if (response.hasDuplicates) {
          console.warn(
            `⚠️ Found ${response.duplicates.length} billing keys with multiple invoices:`,
            response.duplicates
          );
        }

        if (response.hasMixedRevisions) {
          console.warn(
            `⚠️ Found ${response.mixedRevisions.length} billing keys with mixed revisions:`,
            response.mixedRevisions
          );
        }
      } else {
        setBillingKeyValue('sBillingKeys', '');
        console.log(`ℹ️ No outdated invoices found for clinic ${formData.clinicId}`);
      }
    } catch (error: any) {
      console.error('❌ Failed to load outdated invoices:', error);
      alert(`Failed to load outdated invoices: ${error.message}`);
    } finally {
      setIsLoadingOutdated(false);
    }
  };

  const handleCountFromBackupTable = async () => {
    const formData = getBackupTableValues();

    if (!formData.tableName || formData.tableName.trim() === '') {
      alert('Please enter a table name');

      return;
    }

    setIsCountingFromTable(true);
    setTableCountResults(null);

    try {
      const response = await getUnrebuiltCountFromTable({
        tableName: formData.tableName.trim(),
      });

      setTableCountResults({
        tableName: formData.tableName.trim(),
        count: response.count,
      });

      console.log(
        `📊 Count results for table ${formData.tableName}: ${response.count} unrebuilt billing keys`
      );
    } catch (error: any) {
      console.error('❌ Failed to count unrebuilt billing keys:', error);
      alert(`Failed to count unrebuilt billing keys: ${error.message}`);
    } finally {
      setIsCountingFromTable(false);
    }
  };

  const handleLoadFromBackupTable = async () => {
    const formData = getBackupTableValues();

    if (!formData.tableName || formData.tableName.trim() === '') {
      alert('Please enter a table name');

      return;
    }

    setIsLoadingFromTable(true);

    try {
      const response = await getUnrebuiltFromTable({
        tableName: formData.tableName.trim(),
        limit: formData.limit || undefined,
      });

      if (response.billingKeys && response.billingKeys.length > 0) {
        const billingKeysText = response.billingKeys.join('\n');
        setBillingKeyValue('sBillingKeys', billingKeysText);

        console.log(
          `✅ Loaded ${response.count} unrebuilt billing keys from table ${formData.tableName}`
        );
      } else {
        setBillingKeyValue('sBillingKeys', '');
        console.log(`ℹ️ No unrebuilt billing keys found in table ${formData.tableName}`);
      }
    } catch (error: any) {
      console.error('❌ Failed to load billing keys from backup table:', error);
      alert(`Failed to load billing keys from backup table: ${error.message}`);
    } finally {
      setIsLoadingFromTable(false);
    }
  };

  return (
    <div className="p-6 space-y-8">
      {/* Force Invoice Status Change Section */}
      <form onSubmit={handleSubmit(onSubmit)}>
        <section className="bg-white border border-gray-400 inset-4 rounded-lg">
          <div className="p-4 flex flex-row justify-between">
            <h1 className="font-extrabold">Force Invoice Status Change</h1>
            <Button
              text="Go!"
              disabled={isFetching}
              loading={isFetching}
              type="submit"
              icon={Icons.puzzle}
            />
          </div>
          <div className="p-4 m-4 border border-gray-400 bg-gray-50 rounded-lg text-sm space-y-4">
            <p className="text-red-500 flex justify-center">* * * EXTREME DANGER AHEAD * * *</p>
            <p>
              This supports moving one or more invoices to a specific status.
              <em className="text-red-500">There are no restrictions.</em> Any target status may be
              chosen. This will clear any issues and notes on the last invoice transition unless the
              option to copy the issues to notes is selected.
            </p>
            <p>
              The current status is optional and may be used as a type of filter. That is, if the
              invoice is not in the selected status, it will be ignored.
            </p>
            <p>There is NO UI at the current time for the response, so open the browser console.</p>
            <p>
              It returns the original invoice, so this can be used to reverse the process
              (painfully, but possible) if the HAR is saved after the run.
            </p>
          </div>
          <div className="p-4 flex flex-col space-y-4">
            <Controller
              name="finalStatus"
              control={control}
              rules={{ required: 'Required.' }}
              render={({ field }) => (
                <Select
                  {...field}
                  label="Final Status"
                  options={AllInvoiceStatusOptions}
                  tooltip="The status to which the invoices will be moved."
                  errors={
                    errors.finalStatus
                      ? ({
                          message: errors.finalStatus.message,
                        } as FormError)
                      : undefined
                  }
                />
              )}
            />
            <Controller
              name="currentStatus"
              control={control}
              render={({ field }) => (
                <Select
                  {...field}
                  label="Current Status"
                  options={[{ value: undefined, text: '- none -' }, ...AllInvoiceStatusOptions]}
                  tooltip={[
                    'When set, this represents the status the invoice must be',
                    'in before it can be moved to the final status. When not set,',
                    'the current status of the invoice is ignored.',
                  ].join(' ')}
                  errors={
                    errors.currentStatus
                      ? ({
                          message: errors.currentStatus.message,
                        } as FormError)
                      : undefined
                  }
                />
              )}
            />
            <Controller
              name="sInvoices"
              control={control}
              rules={{
                validate: {
                  nums: (val: string | undefined, opts): any => {
                    if (!val) return 'Required.';
                    const nums = val.split('\n').map(v => v.trim());

                    if (nums.some(v => isNaN(parseInt(v)))) {
                      return 'Only numbers are allowed.';
                    }

                    return true;
                  },
                },
              }}
              render={({ field }) => (
                <Textarea
                  {...field}
                  label="Invoice IDs"
                  tooltip='One per line. Example: "1234\n5678\n91011"'
                  rows={10}
                  errors={
                    errors.sInvoices
                      ? ({
                          message: errors.sInvoices.message,
                        } as FormError)
                      : undefined
                  }
                />
              )}
            />
            <Controller
              name={`options.convertIssuesToNotes`}
              control={control}
              render={({ field }) => (
                <Checkbox
                  value={field?.value}
                  onChange={field.onChange}
                  label={`Convert issues to notes.`}
                  tooltip="If checked, any issues on the last invoice transition will be copied to notes."
                  className="col-span-2"
                />
              )}
            />
          </div>
        </section>
      </form>

      {/* Divider */}
      <div className="border-t-2 border-gray-300 my-8"></div>

      {/* Billing Key Processing Sections */}
      <div className="space-y-6">
        {/* Upgrade Invoices Section */}
        <section className="bg-white border border-gray-400 inset-4 rounded-lg">
          <div className="p-4 flex flex-row justify-between items-center">
            <div className="flex flex-col">
              <h2 className="font-extrabold">Upgrade Invoices</h2>
              <p className="text-sm text-gray-600 mt-1">
                Load billing keys for invoices that need upgrading (payorId=null, insuranceId=null,
                rev &lt; {INVOICE_REV_CURRENT.toString()})
              </p>
            </div>
            <div className="flex gap-2">
              <Button
                text="Count"
                onClick={handleCountOutdated}
                type="button"
                loading={isLoadingCount}
                disabled={isLoadingCount || isLoadingOutdated || isProcessing}
                icon={Icons.clock}
              />
              <Button
                text="Fetch"
                onClick={handleLoadOutdated}
                type="button"
                loading={isLoadingOutdated}
                disabled={isLoadingOutdated || isLoadingCount || isProcessing}
                icon={Icons.cloudDownload}
              />
            </div>
          </div>

          <div className="p-4 flex flex-row gap-4">
            <Controller
              name="clinicId"
              control={billingKeyControl}
              rules={{}}
              render={({ field }) => (
                <Input
                  {...field}
                  label="Clinic ID"
                  placeholder="Enter clinic ID"
                  disabled={isProcessing || isLoadingOutdated}
                  errors={
                    billingKeyErrors.clinicId
                      ? ({
                          message: billingKeyErrors.clinicId.message,
                        } as FormError)
                      : undefined
                  }
                />
              )}
            />

            <Controller
              name="limit"
              control={billingKeyControl}
              render={({ field }) => (
                <Input
                  {...field}
                  label="Max Results (optional)"
                  type="number"
                  placeholder="100"
                  disabled={isProcessing || isLoadingOutdated}
                  tooltip="Leave empty for all results, or specify a maximum number of billing keys to return"
                />
              )}
            />
          </div>

          {/* Count Results Section */}
          {countResults && (
            <div className="p-4 m-4 border border-blue-400 bg-blue-50 rounded-lg text-sm">
              <div className="flex items-start space-x-2">
                <div className="text-blue-600">📊</div>
                <div className="flex-1">
                  <p className="font-semibold text-blue-800 mb-2">
                    Count Results for Clinic {countResults.clinicId || 'N/A'}
                  </p>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-blue-700">Unique Billing Keys:</span>
                        <span className="font-semibold">
                          {countResults.uniqueBillingKeys.toLocaleString()}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-blue-700">Total Outdated Invoices:</span>
                        <span className="font-semibold">
                          {countResults.totalOutdatedInvoices.toLocaleString()}
                        </span>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span
                          className={
                            countResults.hasDuplicates ? 'text-yellow-700' : 'text-blue-700'
                          }
                        >
                          Duplicate Issues:
                        </span>
                        <span
                          className={`font-semibold ${countResults.hasDuplicates ? 'text-yellow-800' : ''}`}
                        >
                          {countResults.duplicateBillingKeys.toLocaleString()}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span
                          className={
                            countResults.hasMixedRevisions ? 'text-red-700' : 'text-blue-700'
                          }
                        >
                          Mixed Revision Issues:
                        </span>
                        <span
                          className={`font-semibold ${countResults.hasMixedRevisions ? 'text-red-800' : ''}`}
                        >
                          {countResults.mixedRevisionBillingKeys.toLocaleString()}
                        </span>
                      </div>
                    </div>
                  </div>
                  {(countResults.hasDuplicates || countResults.hasMixedRevisions) && (
                    <div className="mt-3 p-2 bg-orange-100 border border-orange-300 rounded text-xs text-orange-800">
                      <strong>⚠️ Issues detected!</strong> Use "Fetch" to see detailed warnings
                      before processing.
                    </div>
                  )}
                  {!countResults.hasDuplicates &&
                    !countResults.hasMixedRevisions &&
                    countResults.uniqueBillingKeys > 0 && (
                      <div className="mt-3 p-2 bg-green-100 border border-green-300 rounded text-xs text-green-800">
                        <strong>✅ Clean data!</strong> No duplicate or mixed revision issues
                        detected.
                      </div>
                    )}
                </div>
              </div>
            </div>
          )}

          {/* Warning Sections */}
          {(duplicateWarnings.length > 0 || mixedRevisionWarnings.length > 0) && (
            <div className="p-4 m-4 space-y-4">
              {/* Duplicate Warning Section */}
              {duplicateWarnings.length > 0 && (
                <div className="border border-yellow-400 bg-yellow-50 rounded-lg p-4 text-sm">
                  <div className="flex items-start space-x-2">
                    <div className="text-yellow-600">⚠️</div>
                    <div className="flex-1">
                      <p className="font-semibold text-yellow-800 mb-2">
                        Data Integrity Warning: Multiple Invoices Found
                      </p>
                      <p className="text-yellow-700 mb-3">
                        The following billing keys have multiple invoices with null
                        payorId/insuranceId and outdated rev. This violates uniqueness assumptions:
                      </p>
                      <div className="space-y-1 max-h-32 overflow-y-auto">
                        {duplicateWarnings.map((dup, index) => (
                          <div
                            key={index}
                            className="font-mono text-xs bg-yellow-100 px-2 py-1 rounded border"
                          >
                            <span className="font-semibold">{dup.billingKey}</span> (
                            {dup.totalInvoices} total, {dup.outdatedCount} outdated)
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Mixed Revision Warning Section */}
              {mixedRevisionWarnings.length > 0 && (
                <div className="border border-red-400 bg-red-50 rounded-lg p-4 text-sm">
                  <div className="flex items-start space-x-2">
                    <div className="text-red-600">🚨</div>
                    <div className="flex-1">
                      <p className="font-semibold text-red-800 mb-2">
                        Critical Issue: Partial Upgrade Detected
                      </p>
                      <p className="text-red-700 mb-3">
                        The following billing keys have invoices with mixed revision levels. Some
                        are already upgraded while others are not - this indicates a partial
                        upgrade:
                      </p>
                      <div className="space-y-1 max-h-32 overflow-y-auto">
                        {mixedRevisionWarnings.map((mixed, index) => (
                          <div
                            key={index}
                            className="font-mono text-xs bg-red-100 px-2 py-1 rounded border"
                          >
                            <span className="font-semibold">{mixed.billingKey}</span>
                            <span className="ml-2">
                              ({mixed.totalInvoices} invoices: {mixed.outdatedCount} old,{' '}
                              {mixed.currentCount} current)
                            </span>
                            <br />
                            <span className="text-red-600">
                              Revisions: {mixed.allRevs} (min: {mixed.minRev}, max: {mixed.maxRev})
                            </span>
                          </div>
                        ))}
                      </div>
                      <p className="text-red-700 mt-2 text-xs font-medium">
                        ⚠️ This suggests incomplete previous upgrades. Consider manual investigation
                        before bulk processing.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </section>

        {/* Load from Backup Table Section */}
        <section className="bg-white border border-gray-400 inset-4 rounded-lg">
          <div className="p-4 flex flex-row justify-between items-center">
            <div className="flex flex-col">
              <h2 className="font-extrabold">Load from Finalization Backup Table</h2>
              <p className="text-sm text-gray-600 mt-1">
                Load unrebuilt billing keys from a finalization backup table (e.g.,
                PatientTransaction_finalize_20251031_195505_clinic_53504)
              </p>
            </div>
            <div className="flex gap-2">
              <Button
                text="Count"
                onClick={handleCountFromBackupTable}
                type="button"
                loading={isCountingFromTable}
                disabled={isCountingFromTable || isLoadingFromTable || isProcessing}
                icon={Icons.clock}
              />
              <Button
                text="Load"
                onClick={handleLoadFromBackupTable}
                type="button"
                loading={isLoadingFromTable}
                disabled={isLoadingFromTable || isCountingFromTable || isProcessing}
                icon={Icons.cloudDownload}
              />
            </div>
          </div>

          <div className="p-4 flex flex-row gap-4">
            <Controller
              name="tableName"
              control={backupTableControl}
              render={({ field }) => (
                <Input
                  {...field}
                  label="Backup Table Name"
                  type="text"
                  placeholder="PatientTransaction_finalize_20251031_195505_clinic_53504"
                  disabled={isProcessing || isLoadingFromTable}
                  className="flex-grow"
                  errors={
                    backupTableErrors.tableName
                      ? ({
                          message: backupTableErrors.tableName.message,
                        } as FormError)
                      : undefined
                  }
                />
              )}
            />

            <Controller
              name="limit"
              control={backupTableControl}
              render={({ field }) => (
                <Input
                  {...field}
                  label="Max Results (optional)"
                  type="number"
                  placeholder="Leave empty for all"
                  disabled={isProcessing || isLoadingFromTable}
                  tooltip="Leave empty for all unrebuilt billing keys, or specify a maximum number"
                />
              )}
            />
          </div>

          {/* Count Results Section */}
          {tableCountResults && (
            <div className="p-4 m-4 border border-blue-400 bg-blue-50 rounded-lg text-sm">
              <div className="flex items-start space-x-2">
                <div className="text-blue-600">
                  <span role="img" aria-label="chart">
                    📊
                  </span>
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-blue-800 mb-2">
                    Count Results for Table {tableCountResults.tableName}
                  </p>
                  <div className="text-sm">
                    <div className="flex justify-between">
                      <span className="text-blue-700">Unrebuilt Billing Keys:</span>
                      <span className="font-semibold">
                        {tableCountResults.count.toLocaleString()}
                      </span>
                    </div>
                  </div>
                  {tableCountResults.count === 0 && (
                    <div className="mt-3 p-2 bg-green-100 border border-green-300 rounded text-xs text-green-800">
                      <strong>
                        <span role="img" aria-label="checkmark">
                          ✅
                        </span>{' '}
                        All done!
                      </strong>{' '}
                      No unrebuilt billing keys remaining in this table.
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          <div className="p-4 m-4 border border-blue-400 bg-blue-50 rounded-lg text-sm space-y-2">
            <p>
              <strong>How this works:</strong>
            </p>
            <ul className="list-disc list-inside ml-4 space-y-1">
              <li>
                Enter the name of a backup table created by the finalization stored procedure.
              </li>
              <li>
                Loads all billing keys where <code>rebuilt != 1</code>.
              </li>
              <li>Populates the billing keys textarea below for rebuilding.</li>
              <li>
                After successful rebuild, those billing keys are automatically marked as{' '}
                <code>rebuilt = 1</code> in the backup table.
              </li>
            </ul>
          </div>
        </section>

        {/* Billing Key Processing Section */}
        <form onSubmit={handleBillingKeySubmit(onBillingKeySubmit)}>
          <section className="bg-white border border-gray-400 inset-4 rounded-lg">
            <div className="p-4 flex flex-row justify-between items-center">
              <div className="flex flex-col">
                <h1 className="font-extrabold">Rebuild Invoices from Billing Keys</h1>
                {isProcessing && (
                  <div className="text-sm text-gray-600 mt-1 space-y-1">
                    <div>
                      Processing {currentIndex}/
                      {results.length +
                        (getBillingKeyValues('sBillingKeys')
                          ?.split('\n')
                          .filter(l => l.trim()).length || 0) -
                        results.length}{' '}
                      billing keys...
                    </div>
                    {batchStartTime && (
                      <div className="font-mono text-xs">
                        ⏱️ Batch time: {formatDuration(Date.now() - batchStartTime)}
                        {results.length > 0 &&
                          ` | Avg: ${formatDuration(results.reduce((sum, r) => sum + (r.durationMs || 0), 0) / results.length)}`}
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div className="flex gap-2 items-center">
                {isProcessing ? (
                  <Button text="Stop" onClick={handleStop} type="button" icon={Icons.pause} />
                ) : (
                  <>
                    <div className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded border">
                      Batch: {batchSize || 5}
                    </div>
                    <Button
                      text="Go!"
                      disabled={isProcessing}
                      loading={isProcessing}
                      type="submit"
                      icon={Icons.puzzle}
                    />
                  </>
                )}
              </div>
            </div>

            <div className="p-4 m-4 border border-gray-400 bg-gray-50 rounded-lg text-sm space-y-4">
              <p className="text-red-500 flex justify-center">* * * DANGER AHEAD * * *</p>
              <p>
                This will run the <code>onUpgradeInvoice</code> purchase magic event on each billing
                key to rebuild all associated invoices. Billing keys are processed in configurable
                batches to optimize performance and avoid Lambda timeouts.
              </p>
              <p>
                <strong>Supported formats:</strong>
              </p>
              <ul className="list-disc list-inside ml-4 space-y-1">
                <li>
                  Direct billing key: <code>e7fb2f8d-90c3-4df1-948c-4e3c63330849</code>
                </li>
                <li>
                  URL format:{' '}
                  <code>/consolidated/transaction/e7fb2f8d-90c3-4df1-948c-4e3c63330849#5</code>
                </li>
              </ul>
              <p>
                You can stop processing at any time using the Stop button. Progress is shown in
                real-time in the browser console and results section below. Processed items will be
                commented out with '# ' in the textarea, and commented lines will be ignored on
                subsequent runs.
              </p>
            </div>

            <div className="p-4 flex flex-col space-y-4">
              <Controller
                name="batchSize"
                control={billingKeyControl}
                rules={{
                  validate: {
                    range: (val: number | undefined): any => {
                      if (val && (val < 1 || val > 10)) {
                        return 'Batch size must be between 1 and 10.';
                      }

                      return true;
                    },
                  },
                }}
                render={({ field }) => (
                  <Input
                    {...field}
                    label="Batch Size (for processing)"
                    type="number"
                    placeholder="5"
                    min="1"
                    max="10"
                    disabled={isProcessing}
                    tooltip="Number of billing keys to process per REST call (1-10). Larger batches may be faster but risk Lambda timeout."
                    errors={
                      billingKeyErrors.batchSize
                        ? ({
                            message: billingKeyErrors.batchSize.message,
                          } as FormError)
                        : undefined
                    }
                  />
                )}
              />

              <Controller
                name="sBillingKeys"
                control={billingKeyControl}
                rules={{
                  validate: {
                    required: (val: string | undefined): any => {
                      if (!val || val.trim() === '') return 'Required.';

                      return true;
                    },
                  },
                }}
                render={({ field }) => (
                  <Textarea
                    {...field}
                    label="Billing Keys or URLs"
                    tooltip='One per line. Supports both direct keys and URLs. Processed items are commented out with "# " and ignored on subsequent runs.'
                    rows={10}
                    disabled={isProcessing}
                    errors={
                      billingKeyErrors.sBillingKeys
                        ? ({
                            message: billingKeyErrors.sBillingKeys.message,
                          } as FormError)
                        : undefined
                    }
                  />
                )}
              />
            </div>

            {results.length > 0 && (
              <div className="p-4 border-t border-gray-200">
                <h3 className="font-bold mb-3">Processing Results ({results.length})</h3>
                <div className="max-h-96 overflow-y-auto space-y-2">
                  {results.map((result, index) => (
                    <div
                      key={index}
                      className={`p-3 rounded border text-sm ${
                        result.success
                          ? 'bg-green-50 border-green-200'
                          : result.warnings && result.warnings.length > 0
                            ? 'bg-yellow-50 border-yellow-200'
                            : 'bg-red-50 border-red-200'
                      }`}
                    >
                      <div className="font-mono text-xs text-gray-600 mb-1">
                        Input: {result.input}
                      </div>
                      <div className="font-mono text-xs text-gray-600 mb-1">
                        Billing Key: {result.billingKey}
                      </div>
                      <div
                        className={`font-semibold ${
                          result.success
                            ? 'text-green-700'
                            : result.warnings &&
                                result.warnings.length > 0 &&
                                !result.errors?.length
                              ? 'text-yellow-700'
                              : 'text-red-700'
                        }`}
                      >
                        {result.success
                          ? '✅ Success'
                          : result.warnings && result.warnings.length > 0 && !result.errors?.length
                            ? '⚠️ Completed with Issues'
                            : '❌ Failed'}
                        {result.durationMs && ` (${formatDuration(result.durationMs)})`}
                        {result.error && `: ${result.error}`}
                        {result.touched && ' (Data Modified)'}
                      </div>

                      {/* Show magic actions */}
                      {result.actions && result.actions.length > 0 && (
                        <div className="mt-2 space-y-1">
                          <div className="font-semibold text-xs text-gray-700">Magic Actions:</div>
                          {result.actions.map((action: any, actionIndex: number) => (
                            <div
                              key={actionIndex}
                              className={`text-xs px-2 py-1 rounded ${
                                action.type === 'warn'
                                  ? 'bg-yellow-100 text-yellow-800'
                                  : action.type === 'error' || action.type === 'fatal'
                                    ? 'bg-red-100 text-red-800'
                                    : 'bg-blue-100 text-blue-800'
                              }`}
                            >
                              <span className="font-semibold">{action.type?.toUpperCase()}:</span>{' '}
                              {action.message}
                              {action.spellName && (
                                <span className="text-gray-600"> (from {action.spellName})</span>
                              )}
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Show summary stats */}
                      <div className="mt-2 text-xs text-gray-600">
                        Actions: {result.actions?.length || 0} | Warnings:{' '}
                        {result.warnings?.length || 0} | Errors: {result.errors?.length || 0} |
                        Touched: {result.touched ? 'Yes' : 'No'}
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-3 text-sm text-gray-600 space-y-1">
                  <div>
                    Total: {results.length} | Successful: {results.filter(r => r.success).length} |
                    With Issues:{' '}
                    {
                      results.filter(
                        r =>
                          !r.success &&
                          r.warnings &&
                          r.warnings.length > 0 &&
                          (!r.errors || r.errors.length === 0)
                      ).length
                    }{' '}
                    | Failed:{' '}
                    {
                      results.filter(
                        r =>
                          !r.success &&
                          (!r.warnings ||
                            r.warnings.length === 0 ||
                            (r.errors && r.errors.length > 0))
                      ).length
                    }
                  </div>
                  {batchDuration && (
                    <div className="font-mono">
                      ⏱️ Batch Duration: {formatDuration(batchDuration)} | Avg per item:{' '}
                      {formatDuration(
                        results.reduce((sum, r) => sum + (r.durationMs || 0), 0) /
                          Math.max(results.length, 1)
                      )}{' '}
                      | Processing:{' '}
                      {formatDuration(results.reduce((sum, r) => sum + (r.durationMs || 0), 0))} |
                      Overhead:{' '}
                      {formatDuration(
                        batchDuration - results.reduce((sum, r) => sum + (r.durationMs || 0), 0)
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}
          </section>
        </form>
      </div>
    </div>
  );
};

export default InvoicePanel;
